import { MuseumApp } from "./app";
import { loadExhibitionRegistry } from "./asset-loader";
import type { ExhibitionRegistry } from "./contracts";
import {
  renderExhibitionMenu,
  renderRegistryFailure,
  renderUnknownExhibition,
} from "./exhibition-menu";
import { copyFor, readLanguage, storeLanguage, type Language } from "./i18n";
import { readExhibitionSelection, selectionUrl } from "./selection";

export class MuseumShell {
  readonly #root: HTMLElement;
  #registry: ExhibitionRegistry | null = null;
  #app: MuseumApp | null = null;
  #activeSlug: string | null = null;
  #focusSlug: string | null = null;

  constructor(root: HTMLElement) {
    this.#root = root;
    window.addEventListener("popstate", () => this.#route());
  }

  async start(): Promise<void> {
    this.#renderLoading();
    try {
      this.#registry = await loadExhibitionRegistry();
      this.#route();
    } catch {
      const copy = this.#copy();
      renderRegistryFailure(this.#root, copy, () => void this.start());
    }
  }

  #route(force = false): void {
    const registry = this.#registry;
    if (registry === null) return;
    const selection = readExhibitionSelection();
    if (
      !force &&
      this.#app !== null &&
      selection.kind === "exhibition" &&
      selection.slug === this.#activeSlug
    ) {
      return;
    }
    this.#disposeViewer();
    const copy = this.#copy();
    if (selection.kind === "menu") {
      document.title = `${copy.exhibitionsTitle} — ${copy.museumEyebrow}`;
      renderExhibitionMenu(
        this.#root,
        registry,
        readLanguage(),
        copy,
        {
          onLanguage: (language) => this.#changeLanguage(language),
          onSelect: (slug) => this.#select(slug),
        },
        this.#focusSlug,
      );
      this.#focusSlug = null;
      return;
    }
    if (selection.kind === "invalid") {
      renderUnknownExhibition(this.#root, copy, () => this.#backToMenu());
      return;
    }
    const exhibition = registry.exhibitions.find((candidate) => candidate.slug === selection.slug);
    if (exhibition === undefined) {
      renderUnknownExhibition(this.#root, copy, () => this.#backToMenu());
      return;
    }
    this.#focusSlug = exhibition.slug;
    this.#activeSlug = exhibition.slug;
    this.#app = new MuseumApp(this.#root, exhibition, () => this.#backToMenu());
    void this.#app.start();
  }

  #select(slug: string): void {
    history.pushState(null, "", selectionUrl(slug));
    this.#route();
  }

  #backToMenu(): void {
    history.pushState(null, "", selectionUrl(null));
    this.#route();
  }

  #changeLanguage(language: Language): void {
    storeLanguage(language);
    this.#route(true);
  }

  #copy() {
    const copy = copyFor(readLanguage());
    document.documentElement.lang = copy.locale;
    return copy;
  }

  #disposeViewer(): void {
    this.#app?.dispose();
    this.#app = null;
    this.#activeSlug = null;
    this.#root.replaceChildren();
  }

  #renderLoading(): void {
    const copy = this.#copy();
    this.#root.innerHTML = `
      <main class="exhibition-index exhibition-index--message">
        <section class="panel"><p role="status">${copy.preparing}</p></section>
      </main>`;
  }
}
